import { AT_MENTION_SEED_USERS, type AtMentionEntity } from '@/config/static/mentions';
import { normalizeEmail } from '@/config/authAllowlist';
import { PROVISIONED_USERS_CHANGED_EVENT, STORAGE_KEYS } from '@/utils/constants';
import {
  isProvisionedUserGroup,
  type ProvisionedUser,
  type ProvisionedUserGroup,
} from '@/types/provisionedUser';
import type { UserDraft } from '@/types/userCreation';

function subtitleToUserGroup(subtitle: string): ProvisionedUserGroup {
  const lower = subtitle.toLowerCase();
  if (lower.includes('admin')) return 'Administrators';
  if (lower.includes('manager') || lower.includes('operator')) return 'Operators';
  if (lower.includes('viewer') || lower === 'user' || lower.includes('credential')) return 'Viewers';
  if (isProvisionedUserGroup(subtitle)) return subtitle;
  return 'Viewers';
}

function seedProvisionedUsers(): ProvisionedUser[] {
  const now = new Date(0).toISOString();
  return AT_MENTION_SEED_USERS.map((entity) => {
    const parts = entity.name.trim().split(/\s+/);
    const firstName = parts[0] ?? entity.name;
    const lastName = parts.slice(1).join(' ');
    return {
      id: entity.id,
      firstName,
      lastName,
      displayName: entity.name,
      email: '',
      userGroup: subtitleToUserGroup(entity.subtitle),
      createdAt: now,
      createdByEmail: 'system',
      status: 'active' as const,
    };
  });
}

function readStoredUsers(): ProvisionedUser[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PROVISIONED_USERS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ProvisionedUser[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredUsers(users: ProvisionedUser[]) {
  localStorage.setItem(STORAGE_KEYS.PROVISIONED_USERS, JSON.stringify(users));
}

export function notifyProvisionedUsersChanged() {
  window.dispatchEvent(new Event(PROVISIONED_USERS_CHANGED_EVENT));
}

function mergeUsers(seed: ProvisionedUser[], stored: ProvisionedUser[]): ProvisionedUser[] {
  const byId = new Map<string, ProvisionedUser>();
  for (const user of seed) {
    byId.set(user.id, user);
  }
  for (const user of stored) {
    const existing = byId.get(user.id);
    if (existing && !existing.email && user.email) {
      byId.set(user.id, { ...existing, ...user });
    } else {
      byId.set(user.id, user);
    }
  }
  const byEmail = new Map<string, ProvisionedUser>();
  for (const user of byId.values()) {
    const key = user.email ? normalizeEmail(user.email) : `__id:${user.id}`;
    const prev = byEmail.get(key);
    if (!prev || (user.email && !prev.email)) {
      byEmail.set(key, user);
    }
  }
  return Array.from(byEmail.values());
}

export function listUsers(): ProvisionedUser[] {
  return mergeUsers(seedProvisionedUsers(), readStoredUsers());
}

export function getUserById(id: string): ProvisionedUser | undefined {
  return listUsers().find((u) => u.id === id);
}

export function getDynamicAllowedEmails(): string[] {
  return listUsers()
    .map((u) => u.email.trim())
    .filter(Boolean)
    .map((email) => normalizeEmail(email));
}

export function toMentionEntities(): AtMentionEntity[] {
  return listUsers().map((user) => ({
    id: user.id,
    name: user.displayName,
    subtitle: user.userGroup === 'None' ? 'Viewers' : user.userGroup,
  }));
}

export function registerFromDraft(draft: UserDraft, createdByEmail: string): ProvisionedUser | null {
  const email = normalizeEmail(draft.email);
  if (!email) return null;

  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const displayName = `${firstName} ${lastName}`.trim() || email;
  const userGroup = isProvisionedUserGroup(draft.userGroup) ? draft.userGroup : 'None';

  const stored = readStoredUsers();
  const existingIndex = stored.findIndex((u) => u.email && normalizeEmail(u.email) === email);

  const record: ProvisionedUser = {
    id: existingIndex >= 0 ? stored[existingIndex].id : `user-${crypto.randomUUID()}`,
    firstName,
    lastName,
    displayName,
    email,
    userGroup,
    employmentNumber: draft.employmentNumber || undefined,
    createdAt: existingIndex >= 0 ? stored[existingIndex].createdAt : new Date().toISOString(),
    createdByEmail: normalizeEmail(createdByEmail),
    status: 'active',
  };

  if (existingIndex >= 0) {
    stored[existingIndex] = record;
  } else {
    stored.push(record);
  }

  writeStoredUsers(stored);
  notifyProvisionedUsersChanged();
  return record;
}

/** Clear dynamic users (for tests). */
export function clearStoredProvisionedUsers() {
  localStorage.removeItem(STORAGE_KEYS.PROVISIONED_USERS);
  notifyProvisionedUsersChanged();
}
